import { createLimiter } from './limiter';
import { METER_SILENCE_DB, peakToDb } from '@/state/meter';

const METER_FFT_SIZE = 1024;   // ~23 ms window at 44.1 kHz, one read per frame

export type EngineEvent = 'ready' | 'play' | 'pause' | 'timeupdate' | 'seek';

const RAMP_S = 0.005;

/**
 * Multi-source synchronized playback engine.
 *
 * Owns a single AudioContext, one AudioBuffer per source (the model's stems, or
 * the before/after pair of a Denoise model), per-source GainNodes, and a
 * master DynamicsCompressor acting as a soft limiter. Playback is loop-seamless:
 * all sources share loopStart/loopEnd and are started with a single scheduling
 * call so they remain sample-accurate regardless of buffer slight-length
 * differences (we clamp all loops to the shortest buffer's duration).
 *
 * The engine knows nothing about sliders, mute or solo: the widget derives one
 * linear gain per source from its mix state (state/mix.ts sourceGains) and
 * hands the whole vector to setGains(). Gains above 1 (dB sliders go to +12)
 * are expected; the limiter is the safety net, as in the app.
 */
export class AudioEngine {
  private ctx: AudioContext;
  private buffers: AudioBuffer[] = [];
  private sources: AudioBufferSourceNode[] = [];
  private gains: GainNode[] = [];
  private master: DynamicsCompressorNode;

  private isPlaying_ = false;
  private startCtxTime = 0; // context time when current play session started
  private pauseOffset = 0; // seconds into the buffer (pre-modulo)
  private duration_ = 0;

  private listeners = new Map<EngineEvent, Set<() => void>>();
  private rafHandle: number | null = null;

  private targetGains: number[] = [];

  // Level meter tap: a splitter after the master limiter feeding one
  // AnalyserNode per channel. Created on first read, so a page that never
  // shows the meter pays nothing.
  private meterAnalysers: AnalyserNode[] = [];
  private meterBuf: Float32Array<ArrayBuffer> | null = null;

  constructor() {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = createLimiter(this.ctx);
    this.master.connect(this.ctx.destination);
  }

  get context(): AudioContext {
    return this.ctx;
  }

  get duration(): number {
    return this.duration_;
  }

  get isPlaying(): boolean {
    return this.isPlaying_;
  }

  get currentTime(): number {
    if (!this.isPlaying_) {
      // A paused position may sit exactly at the end (seekToEnd) — keep it
      // there rather than wrapping to 0 like the playing branch does.
      return Math.min(this.pauseOffset, this.duration_);
    }
    const elapsed = this.ctx.currentTime - this.startCtxTime + this.pauseOffset;
    return this.duration_ > 0 ? elapsed % this.duration_ : 0;
  }

  on(event: EngineEvent, cb: () => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(event: EngineEvent): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of set) cb();
  }

  async setBuffers(buffers: AudioBuffer[], gains?: number[]): Promise<void> {
    this.stopInternal();
    this.buffers = buffers;
    // Clamp duration to the shortest buffer so loop wrap is consistent.
    this.duration_ = Math.min(...buffers.map((b) => b.duration));

    // Build gain nodes (one per source).
    for (const g of this.gains) g.disconnect();
    this.gains = buffers.map(() => {
      const g = this.ctx.createGain();
      g.gain.value = 1;
      g.connect(this.master);
      return g;
    });

    this.targetGains = gains ? gains.slice() : buffers.map(() => 1);
    this.applyGains(true);
    this.pauseOffset = 0;
    this.emit('ready');
    this.emit('timeupdate');
  }

  // One linear gain per source, in buffer order. Ramped over RAMP_S so slider
  // drags and mute/solo clicks never click.
  setGains(gains: number[]): void {
    this.targetGains = gains.slice();
    this.applyGains(false);
  }

  private applyGains(immediate: boolean): void {
    const now = this.ctx.currentTime;
    for (let i = 0; i < this.gains.length; i++) {
      const g = this.targetGains[i] ?? 1;
      if (immediate) {
        this.gains[i].gain.setValueAtTime(g, now);
      } else {
        this.gains[i].gain.setTargetAtTime(g, now, RAMP_S);
      }
    }
  }

  // Peak of what is reaching the output since the last frame, per channel,
  // in dBFS. Mono material reports the same value on both channels.
  readPeakDb(): [number, number] {
    if (this.meterAnalysers.length === 0) {
      const splitter = this.ctx.createChannelSplitter(2);
      this.master.connect(splitter);
      this.meterAnalysers = [0, 1].map((ch) => {
        const a = this.ctx.createAnalyser();
        a.fftSize = METER_FFT_SIZE;
        splitter.connect(a, ch);
        return a;
      });
      this.meterBuf = new Float32Array(METER_FFT_SIZE);
    }
    const buf = this.meterBuf!;
    const out: [number, number] = [METER_SILENCE_DB, METER_SILENCE_DB];
    const mono = this.buffers[0]?.numberOfChannels === 1;
    for (let ch = 0; ch < (mono ? 1 : 2); ch++) {
      this.meterAnalysers[ch].getFloatTimeDomainData(buf);
      let peak = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i] < 0 ? -buf[i] : buf[i];
        if (v > peak) peak = v;
      }
      out[ch] = peakToDb(peak);
    }
    if (mono) out[1] = out[0];
    return out;
  }

  async resume(): Promise<void> {
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
  }

  async play(): Promise<void> {
    if (this.isPlaying_ || this.buffers.length === 0) return;
    await this.resume();
    this.scheduleSources(this.pauseOffset % this.duration_);
    this.isPlaying_ = true;
    this.emit('play');
    this.startRaf();
  }

  pause(): void {
    if (!this.isPlaying_) return;
    const t = this.currentTime;
    this.stopInternal();
    this.pauseOffset = t;
    this.isPlaying_ = false;
    this.stopRaf();
    this.emit('pause');
    this.emit('timeupdate');
  }

  seek(time: number): void {
    const wasPlaying = this.isPlaying_;
    this.stopInternal();
    this.pauseOffset = Math.max(0, Math.min(this.duration_, time));
    this.isPlaying_ = false;
    if (wasPlaying) {
      this.scheduleSources(this.pauseOffset % this.duration_);
      this.isPlaying_ = true;
      this.emit('play');
      this.startRaf();
    }
    this.emit('seek');
    this.emit('timeupdate');
  }

  seekToStart(): void {
    this.seek(0);
  }

  // Photon's |> button: pause with the playhead parked at the end; the next
  // play starts from the top (the paused offset wraps in play()).
  seekToEnd(): void {
    const wasPlaying = this.isPlaying_;
    this.stopInternal();
    this.pauseOffset = this.duration_;
    this.isPlaying_ = false;
    this.stopRaf();
    if (wasPlaying) this.emit('pause');
    this.emit('seek');
    this.emit('timeupdate');
  }

  private scheduleSources(offset: number): void {
    for (const s of this.sources) {
      try { s.stop(); } catch { /* already stopped */ }
      s.disconnect();
    }
    this.sources = this.buffers.map((buffer, i) => {
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.loopStart = 0;
      src.loopEnd = this.duration_;
      src.connect(this.gains[i]);
      return src;
    });
    this.startCtxTime = this.ctx.currentTime;
    const startAt = this.ctx.currentTime; // immediate
    for (const src of this.sources) {
      src.start(startAt, offset);
    }
  }

  private stopInternal(): void {
    for (const s of this.sources) {
      try { s.stop(); } catch { /* already stopped */ }
      s.disconnect();
    }
    this.sources = [];
  }

  private startRaf(): void {
    if (this.rafHandle !== null) return;
    const tick = () => {
      this.emit('timeupdate');
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private stopRaf(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  async dispose(): Promise<void> {
    this.stopRaf();
    this.stopInternal();
    this.listeners.clear();
    try {
      await this.ctx.close();
    } catch { /* ignore */ }
  }
}
